import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { Info } from 'lucide-react';
import { formatPercent } from '@shared';
import { PhaseInflationScheduleRow } from '@client/util/getPhaseInflationSchedule';

interface PhaseInflationScheduleTooltipProps {
  inflationRate: number;
  schedule: PhaseInflationScheduleRow[];
  phaseInflationFactor: number;
}

// Explains the engine's annually-stepped inflation for one care phase: each
// care month is priced in today's dollars grown by the whole-year multiplier
// for when that month occurs, so the phase factor is the month-weighted
// average of the rows below. Month counts per row keep a sub-year phase that
// straddles a step boundary from reading as multiple years of care.
export function PhaseInflationScheduleTooltip({
  inflationRate,
  schedule,
  phaseInflationFactor,
}: PhaseInflationScheduleTooltipProps) {
  const totalMonths = schedule.reduce((sum, row) => sum + row.months, 0);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Info className="text-muted-foreground size-3.5 shrink-0 cursor-help" />
      </TooltipTrigger>
      <TooltipContent className="max-w-xs space-y-2">
        <p>
          Costs are estimated in today&apos;s dollars and grow{' '}
          {formatPercent(inflationRate, 1)} per year, compounding annually until the care occurs.
          Each month of care uses the multiplier for the year it falls in:
        </p>
        {schedule.length > 0 && (
          <table>
            <tbody>
              {schedule.map(row => (
                <tr key={`${row.careYear}-${row.yearsOfInflation}`}>
                  <td className="pr-3 font-medium">{row.careYear}</td>
                  <td className="pr-3">
                    {row.months} {row.months === 1 ? 'month' : 'months'}
                  </td>
                  <td className="pr-3">
                    {row.yearsOfInflation} {row.yearsOfInflation === 1 ? 'yr' : 'yrs'} of growth
                  </td>
                  <td className="text-right">×{row.multiplier.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p>
          The {formatPercent(phaseInflationFactor, 1)} shown is the average multiplier (×
          {phaseInflationFactor.toFixed(2)}) across
          {totalMonths > 0 ? ` the phase's ${totalMonths} care months` : ' this phase'}.
        </p>
      </TooltipContent>
    </Tooltip>
  );
}
